/**
 * ComplianceGauge Component
 *
 * SVG half-circle speedometer gauge showing assessment coverage percentage.
 * Uses colored zones (red/amber/green) with an animated needle.
 */

function ComplianceGauge({ percentage = 0, label = 'Assessment Coverage', size = 200 }) {
  const cx = size / 2
  const cy = size / 2 + 10
  const radius = size / 2 - 20
  const strokeWidth = 18

  // Arc goes from 180° (left) to 0° (right) — a half circle
  const startAngle = 180
  const endAngle = 0
  const totalSweep = startAngle - endAngle

  // Convert angle to radians for SVG arc calculations
  function polarToCartesian(angle) {
    const rad = (angle * Math.PI) / 180
    return {
      x: cx + radius * Math.cos(rad),
      y: cy - radius * Math.sin(rad)
    }
  }

  // Create arc path
  function describeArc(start, end) {
    const s = polarToCartesian(start)
    const e = polarToCartesian(end)
    const largeArc = start - end > 180 ? 1 : 0
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${largeArc} 1 ${e.x} ${e.y}`
  }

  // Needle angle based on percentage — aligned with end of filled arc
  const needleAngle = startAngle - (percentage / 100) * totalSweep
  const needleRad = (needleAngle * Math.PI) / 180
  // Needle starts with a gap from center dot and ends just inside the arc
  const needleGap = 12
  const needleLength = radius - strokeWidth / 2 - 4
  const needleStartX = cx + needleGap * Math.cos(needleRad)
  const needleStartY = cy - needleGap * Math.sin(needleRad)
  const needleX = cx + needleLength * Math.cos(needleRad)
  const needleY = cy - needleLength * Math.sin(needleRad)

  // Color for the percentage text based on zone
  const textColorClass = percentage < 40
    ? 'text-red-600 dark:text-red-400'
    : percentage < 70
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-green-600 dark:text-green-400'

  return (
    <div className="flex flex-col items-center">
      <svg
        width={size}
        height={size / 2 + 30}
        viewBox={`0 0 ${size} ${size / 2 + 30}`}
        className="overflow-visible"
      >
        {/* Background track */}
        <path
          d={describeArc(180, 0)}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
          strokeLinecap="round"
        />

        {/* Active arc (filled portion) */}
        {percentage > 0 && (
          <path
            d={describeArc(180, needleAngle)}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={
              percentage < 40
                ? 'stroke-red-500 dark:stroke-red-400'
                : percentage < 70
                ? 'stroke-amber-500 dark:stroke-amber-400'
                : 'stroke-green-500 dark:stroke-green-400'
            }
          />
        )}

        {/* Needle — starts with gap from center dot */}
        <line
          x1={needleStartX}
          y1={needleStartY}
          x2={needleX}
          y2={needleY}
          strokeWidth={2.5}
          strokeLinecap="round"
          className="stroke-gray-700 dark:stroke-gray-200"
        />

        {/* Center dot */}
        <circle cx={cx} cy={cy} r={5} className="fill-gray-700 dark:fill-gray-200" />

        {/* Zone labels */}
        <text x={cx - radius} y={cy + 24} className="fill-gray-400 dark:fill-gray-500" fontSize="9" textAnchor="middle">0%</text>
        <text x={cx + radius} y={cy + 24} className="fill-gray-400 dark:fill-gray-500" fontSize="9" textAnchor="middle">100%</text>
      </svg>

      {/* Percentage value */}
      <div className="-mt-2 text-center">
        <span className={`text-2xl font-bold ${textColorClass}`}>
          {percentage}%
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      </div>
    </div>
  )
}

export default ComplianceGauge
